/**
 * SubjectGlyph — Colored gradient square representing a subject.
 *
 * Prefers `subject.logo` (uploaded image) when provided.
 * Falls back to a typographic glyph from `utils/subjects.js`.
 *
 * Sizes:  sm  36px  (compact rails)
 *         md  44px  (exam cards)
 *         lg  54px  (tile headers)
 *
 * The wrapper applies its subject palette class so the gradient picks up
 * --s-c1, --s-c2 and --s-shadow from CSS.
 */
import { glyphFor, paletteFor } from '@/utils/subjects'
import { pickVariant } from '@/utils/imageVariant'

const SIZES = {
  sm: { px: 36, box: 'w-9 h-9 rounded-[10px] text-[18px]',  shadow: 'shadow-md' },
  md: { px: 44, box: 'w-11 h-11 rounded-xl   text-[21px]',  shadow: 'shadow-lg' },
  lg: { px: 54, box: 'w-[54px] h-[54px] rounded-[14px] text-[24px]', shadow: 'shadow-lg' },
}

export default function SubjectGlyph({ subject, examTitle, size = 'md', className = '' }) {
  // When subject is absent, fall back to exam title for palette/glyph derivation
  const palette = paletteFor(subject, examTitle)
  const glyph   = glyphFor(subject, examTitle)
  const s       = SIZES[size]
  // Prefer a display-sized WebP variant, then the original, then the glyph.
  const logo    = pickVariant(subject?.logo_variants, s.px, subject?.logo_url ?? subject?.logo ?? null)

  return (
    <span
      className={
        `${palette} grid place-items-center ${s.box} ${s.shadow} ${className} ` +
        'text-white font-display italic flex-shrink-0 overflow-hidden'
      }
      style={{
        background: 'linear-gradient(135deg, var(--s-c1), var(--s-c2))',
        boxShadow: '0 6px 18px var(--s-shadow)',
      }}
    >
      {logo
        ? <img src={logo} alt="" className="w-full h-full object-cover" />
        : <span aria-hidden="true">{glyph}</span>
      }
    </span>
  )
}
